import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {useExclusiveBundleFlow} from "./../hooks/useExclusiveBundleFlow.js";
import amirCollectionHeroImage from "./../assets/eftakari-collection-hero-image.jpg";
import focusPieceImageOne from "./../assets/focus-piece-one.jpg";
import focusPieceImageTwo from "./../assets/focus-piece-two.jpg";
import focusPieceImageThree from "./../assets/focus-piece-three.jpg";
import focusPieceImageFour from "./../assets/focus-piece-four.jpg";
import focusPieceImageFive from "./../assets/focus-piece-five.jpg";
import focusPieceImageSix from "./../assets/focus-piece-six.jpg";
import focusPieceImageSeven from "./../assets/focus-piece-seven.jpg";
import focusPieceImageEight from "./../assets/focus-piece-eight.jpg";
import focusPieceImageNine from "./../assets/focus-piece-nine.jpg";
import curatedMobileGalleryFour from "./../assets/curated-mobile-gallery-four.jpg";
import curatedMobileGalleryTwo from "./../assets/curated-mobile-gallery-two.jpg";
import { ReactComponent as ApplePayIcon } from "./../assets/apple.svg";
import NewNavbar from "../components/NewNavbar.js";
import Footer from "../components/Footer.js";
import { useNavigate } from "react-router-dom";
import { formatProductPrice, getProductPricing } from "../utils.js";
import { normalizeStockQuantity } from "./../utils/exclusiveBundle.js"
import ExclusiveBundleSizingModal from "../components/ExclusiveBundleSizingModal.js";
import AddedToCartDrawer from "../components/AddedToCartDrawer.js";
import QuickPayButton from "../components/QuickPayButton.js";
import toast from "react-hot-toast";
import {
  addToCart,
  selectStandaloneCartItems,
} from "../store/cartSlice.js";
import { logAddToCart } from "../utils/analytics.js";
import SEO from "../components/SEO.js";

const galleryImages = [
  {
    src: focusPieceImageOne,
    className: "aspect-[0.78]",
  },
  {
    src: focusPieceImageFour,
    className: "aspect-[1.15]",
  },
  {
    src: focusPieceImageTwo,
    className: "aspect-[1.08]",
  },
  {
    src: focusPieceImageFive,
    className: "aspect-[1.08]",
  },
  {
    src: focusPieceImageThree,
    className: "aspect-[1.13]",
  },
  {
    src: focusPieceImageSix,
    className: "aspect-[1.15]",
  },
  {
    src: focusPieceImageSeven,
    className: "aspect-[1.15]",
  },
  {
    src: focusPieceImageEight,
    className: "aspect-[1.15]",
  },
  {
    src: focusPieceImageNine,
    className: "aspect-[1.15]",
  },
  {
    src: curatedMobileGalleryTwo,
    className: "aspect-[1.15]",
  },
  {
    src: curatedMobileGalleryFour,
    className: "aspect-[1.15]",
  },
];

const collectionTranslations = {
  en: {
    languageCode: "EN",
    heroTitle: "Contemporary Fashion, Made in London, Embracing Persia.",
    heroDescription: "Amir Razipour and Amir Eftekhari reinterpret Persian heritage across three limited-edition pieces, bringing traditional motifs into contemporary form.",
    launchDate: "Launch Date",
    collectionCloses: "Collection Closes",
    quote: "I've always carried Iran with me wherever I go. This was a chance to bring that heritage into something new, and let it live in a different language.",
    loadingCollection: "Loading collection...",
    failedCollection: "Failed to load the Eftakhari collection.",
    noProducts: "No collection products are available at the moment. Please check back soon!",
    madeToOrder: "Made to Order in London. Final Sale. No returns.",
    shippingNotice: "Orders ship in 4-6 weeks. Free shipping. Shipping available to the EU and US only.",
    expressPay: "Express pay,",
    bundleCollection: "Bundle the entire Collection for",
    off: "Off.",
    addAllPrefix: "Add All",
    addAllSuffix: "To Cart",
    focusPiece: "Focus Piece",
    story: "The Story",
    storyTitle: "Inspired by the culture we love.",
    finalSaleEyebrow: "One time orders before the collection is gone forever.",
    finalSaleTitle: "10/02/26 Final Sale Date",
    finalSaleDescription: "Alongside Amir Eftekhari, Amir Razipour combines his time spent studying fashion in London at Central St. Martins with inspirations from Eftekhari’s contemporary Persian art form. Each piece serves as an allegory to a modern Persia.",
    addToCart: "Add to Cart",
    goToCart: "Go to Cart",
    selectSize: "Select a Size",
    outOfStock: "Out of Stock",
    modalExpressCheckout: "Express Checkout",
    modalCompleteCollection: "Complete Your Collection",
    modalSelectSizes: "Select your sizes",
    modalApplePayDescription: "Choose a size for each available piece, then continue with Apple Pay.",
    modalCartDescription: "Choose a size for each available piece before adding the collection to your cart.",
    modalSelectSize: "Select size",
    modalContinue: "Select All Sizes To Continue",
    modalSoldOut: "Collection Sold Out",
    modalSelectEveryProduct: "Please select a size for every available product.",
    modalSoldOutSkipped: (count) => `${count} ${count === 1 ? "product is" : "products are"} sold out and will be skipped.`,
    modalNoSizes: "No sizes are currently available.",
    modalSelectedCount: (selected, total) => `${selected} of ${total} available ${total === 1 ? "product" : "products"} selected`,
    modalAddToCart: (count) => `Add ${count} ${count === 1 ? "Product" : "Products"} To Cart`,
    modalClose: "Close sizing modal",
    productDetailsUnavailable: "Product details are not available yet.",
    pleaseSelectSize: "Please select a size.",
    sizeOutOfStock: (size) => `${size || "This size"} is out of stock.`,
    addedToCart: "Added to cart",
    bundleInfoUnavailable: "Bundle information is unavailable.",
    bundleProductsUnavailable: "Some products in this bundle are unavailable.",
    noProductsToAdd: "There are no available products to add.",
    selectEveryBundleSize: "Please select a size for every product in the bundle.",
    bundleAddFailed: "One or more bundle products could not be added.",
    bundleProductUnavailable: (name) => `${name || "A bundle product"} is no longer available in the selected size.`,
    bundleAdded: (name) => `${name} added to cart`,
    bundleInfoCurrentlyUnavailable: "Bundle information is currently unavailable.",
    bundleHasNoProducts: "This bundle does not contain any products.",
    bundleProductsCurrentlyUnavailable: "Some products in this bundle are currently unavailable.",
    bundleProductSoldOut: (name) => `${name || "A bundle product"} is currently sold out.`,
  },

  de: {
    languageCode: "DE",
    heroTitle: "Zeitgenössische Mode, in London gefertigt, mit Persien im Herzen.",
    heroDescription: "Amir Razipour und Amir Eftekhari interpretieren das persische Kulturerbe mit drei Kreationen in limitierter Auflage neu und verleihen traditionellen Motiven eine zeitgenössische Form.",
    launchDate: "Verkaufsstart",
    collectionCloses: "Erhältlich bis",
    quote: "Ich habe den Iran immer bei mir getragen, egal wohin ich ging. Dies war eine Gelegenheit, dieses Erbe in etwas Neues zu bringen und es in einer anderen Sprache weiterleben zu lassen.",
    loadingCollection: "Kollektion wird geladen...",
    failedCollection: "Die Eftakhari-Kollektion konnte nicht geladen werden.",
    noProducts: "Derzeit sind keine Produkte aus der Kollektion verfügbar.",
    madeToOrder: "Auf Bestellung in London gefertigt. Schlussverkauf. Keine Rückgabe.",
    shippingNotice: "Bestellungen werden innerhalb von 4-6 Wochen versandt. Kostenloser Versand. Versand nur in die EU und die USA.",
    expressPay: "Express bezahlen,",
    bundleCollection: "gesamte Kollektion bündeln und",
    off: "sparen.",
    addAllPrefix: "Alle",
    addAllSuffix: "in den Warenkorb",
    focusPiece: "Focus Piece",
    story: "Die Geschichte",
    storyTitle: "Inspiriert von der Kultur, die wir lieben.",
    finalSaleEyebrow: "Inspiriert von der Kultur, die wir lieben.",
    finalSaleTitle: "02.10.26 — Verkaufsende",
    finalSaleDescription: "Gemeinsam mit Amir Eftekhari verbindet Amir Razipour seine Zeit am Central Saint Martins in London mit Inspirationen aus Eftekhari’s zeitgenössischer persischer Kunst. Jedes Stück steht sinnbildlich für ein modernes Persien.",
    addToCart: "In den Warenkorb",
    goToCart: "Zum Warenkorb",
    selectSize: "Größe wählen",
    outOfStock: "Ausverkauft",
    modalExpressCheckout: "Express-Checkout",
    modalCompleteCollection: "Kollektion vervollständigen",
    modalSelectSizes: "Größen auswählen",
    modalApplePayDescription: "Wähle für jedes verfügbare Stück eine Größe und fahre anschließend mit Apple Pay fort.",
    modalCartDescription: "Wähle für jedes verfügbare Stück eine Größe, bevor du die Kollektion in den Warenkorb legst.",
    modalSelectSize: "Größe wählen",
    modalContinue: "Alle Größen auswählen",
    modalSoldOut: "Kollektion ausverkauft",
    modalSelectEveryProduct: "Bitte wähle für jedes verfügbare Produkt eine Größe aus.",
    modalSoldOutSkipped: (count) =>
      `${count} ${
        count === 1 ? "Produkt ist" : "Produkte sind"
      } ausverkauft und werden übersprungen.`,
    modalNoSizes: "Derzeit sind keine Größen verfügbar.",
    modalSelectedCount: (selected, total) =>
      `${selected} von ${total} verfügbaren ${
        total === 1 ? "Produkt" : "Produkten"
      } ausgewählt`,
    modalAddToCart: (count) => `Alle ${count} in den Warenkorb`,
    modalClose: "Größenauswahl schließen",
    productDetailsUnavailable: "Die Produktdetails sind derzeit nicht verfügbar.",
    pleaseSelectSize: "Bitte wähle eine Größe aus.",
    sizeOutOfStock: (size) => `${size || "Diese Größe"} ist ausverkauft.`,
    addedToCart: "Zum Warenkorb hinzugefügt",
    bundleInfoUnavailable: "Die Bundle-Informationen sind nicht verfügbar.",
    bundleProductsUnavailable: "Einige Produkte in diesem Bundle sind nicht verfügbar.",
    noProductsToAdd: "Es sind keine verfügbaren Produkte zum Hinzufügen vorhanden.",
    selectEveryBundleSize: "Bitte wähle für jedes Produkt im Bundle eine Größe aus.",
    bundleAddFailed: "Ein oder mehrere Produkte konnten nicht hinzugefügt werden.",
    bundleProductUnavailable: (name) => `${name || "Ein Produkt"} ist in der ausgewählten Größe nicht mehr verfügbar.`,
    bundleAdded: (name) => `${name} wurde zum Warenkorb hinzugefügt.`,
    bundleInfoCurrentlyUnavailable: "Die Bundle-Informationen sind derzeit nicht verfügbar.",
    bundleHasNoProducts: "Dieses Bundle enthält keine Produkte.",
    bundleProductsCurrentlyUnavailable: "Einige Produkte in diesem Bundle sind derzeit nicht verfügbar.",
    bundleProductSoldOut: (name) => `${name || "Ein Produkt"} ist derzeit ausverkauft.`,
  },
};

const SectionLabel = ({ number, children }) => {
  return (
    <div className="flex items-center gap-2 font-spaceMono text-[10px] uppercase tracking-[0.08em] text-[#6B7280] md:gap-3 md:text-[14px]">
      <span className="text-slate-900">{number}</span>
      <span className="h-px w-4 bg-amber-400 lg:w-5" />
      <span>{children}</span>
    </div>
  );
};

const CollectionProductCard = ({
  piece,
  copy,
  onOpenProduct,
  onProductAddedToCart,
  onGoToCart,
}) => {
  const dispatch = useDispatch();
  const [selectedSize, setSelectedSize] = React.useState(
    piece.defaultSize || piece.sizes?.[0] || "",
  );

  const getVariantForSize = React.useCallback(
    (size) => {
      const matchingVariants = (piece.variants || []).filter(
        (variant) =>
          String(variant?.size || "")
            .trim()
            .toUpperCase() ===
          String(size || "")
            .trim()
            .toUpperCase(),
      );
      return (
        matchingVariants.find((variant) => variant?.isOutOfStock !== true) ||
        matchingVariants[0] ||
        null
      );
    },
    [piece.variants],
  );

  const selectedVariant = React.useMemo(
    () => getVariantForSize(selectedSize),
    [getVariantForSize, selectedSize],
  );
  const cartProductLookupId = selectedVariant?.id
    ? Number(selectedVariant.id)
    : -1;
  const standaloneCartItems = useSelector(selectStandaloneCartItems);

  const isStandaloneInCart = React.useMemo(() => {
    if (cartProductLookupId <= 0) return false;
    return standaloneCartItems.some((item) => {
      const cartProduct = item?.product;
      const cartVariantId = Number(cartProduct?.variant_id ?? cartProduct?.id);
      return cartVariantId === cartProductLookupId;
    });
  }, [standaloneCartItems, cartProductLookupId]);

  const selectedPricing = React.useMemo(() => {
    if (!selectedVariant) return getProductPricing(piece, true);

    const hasVariantDiscountedPrice =
      selectedVariant?.discounted_price !== null &&
      selectedVariant?.discounted_price !== undefined &&
      selectedVariant?.discounted_price !== "";

    const hasVariantDiscount =
      selectedVariant?.discount !== null &&
      selectedVariant?.discount !== undefined &&
      selectedVariant?.discount !== "";

    return getProductPricing(
      {
        price:
          selectedVariant?.original_price ??
          selectedVariant?.price ??
          piece?.original_price ??
          piece?.originalPrice,
        original_price:
          selectedVariant?.original_price ??
          selectedVariant?.price ??
          piece?.original_price ??
          piece?.originalPrice,
        discounted_price: hasVariantDiscountedPrice
          ? selectedVariant.discounted_price
          : piece?.discounted_price,
        discount: hasVariantDiscount
          ? selectedVariant.discount
          : (piece?.discount ?? piece?.discountPercent),
      },
      true,
    );
  }, [piece, selectedVariant]);

  const displayedPrice = formatProductPrice(
    selectedPricing?.discountedPrice ?? piece?.discounted_price ?? piece?.price,
  );
  const selectedVariantQuantity = normalizeStockQuantity(
    selectedVariant?.quantity,
  );
  const isQuickPayDisabled =
    !selectedVariant?.id ||
    selectedVariant?.isOutOfStock === true ||
    (selectedVariantQuantity !== null && selectedVariantQuantity <= 0);

  const quickPayItems = React.useMemo(() => {
    if (isQuickPayDisabled) return [];

    return [
      {
        variant_id: Number(selectedVariant.id),
        quantity: 1,
      },
    ];
  }, [isQuickPayDisabled, selectedVariant?.id]);

  const quickPayAmount = React.useMemo(
    () => Math.round(Number(selectedPricing?.discountedPrice || 0) * 100),
    [selectedPricing?.discountedPrice],
  );

  const handleAddToCart = () => {
    if (!piece?.id) {
      toast.error(copy.productDetailsUnavailable);
      return;
    }
    if (!selectedVariant) {
      toast.error(copy.pleaseSelectSize);
      return;
    }
    const selectedVariantQuantity = normalizeStockQuantity(
      selectedVariant?.quantity,
    );
    if (selectedVariantQuantity !== null && selectedVariantQuantity <= 0) {
      toast.error(copy.sizeOutOfStock(selectedVariant?.size));
      return;
    }

    const numericPrice = selectedPricing.discountedPrice;
    const cartItemId = Number(selectedVariant.id);
    const cartImage = selectedVariant?.image || piece.image || "";

    const cartProduct = {
      ...piece,
      id: cartItemId,
      product_id: Number(piece.id),
      variant_id: cartItemId,
      image: cartImage,
      image_url: cartImage,
      available_quantity: selectedVariant?.quantity,
      price: numericPrice,
      original_price: selectedPricing.originalPrice,
      discounted_price: selectedPricing.discountedPrice,
      discount: selectedPricing.discountPercent,
      discount_percent: selectedPricing.discountPercent,
      savings: selectedPricing.savings,
      variant_original_price:
        selectedVariant?.originalPrice ?? selectedPricing.originalPrice,
      variant_discounted_price:
        selectedVariant?.discountedPrice ?? selectedPricing.discountedPrice,
      variant_discount:
        selectedVariant?.discountPercent ?? selectedPricing.discountPercent,
      size: selectedVariant.size,
      color: selectedVariant.color || piece.color || "",
      main_images: [cartImage].filter(Boolean),
      productType: "exclusive",
      product_type: "exclusive",
      is_marketplace: false,
    };

    try {
      logAddToCart({
        id: cartProduct.id,
        name: cartProduct.name,
        price: numericPrice,
        category: cartProduct.garmentType || "Fashion",
        variant: cartProduct.variant_id,
        size: cartProduct.size,
        quantity: 1,
      });
    } catch (error) {
      console.log("Analytics add to cart logging failed", error);
    }
    dispatch(addToCart({ product: cartProduct, quantity: 1 }));
    onProductAddedToCart?.(cartProduct.id);
    toast.success(copy.addedToCart);
  };

  return (
    <article className="group overflow-hidden rounded-[5px] bg-white">
      {/* PRODUCT IMAGE */}
      <button
        type="button"
        onClick={() => onOpenProduct(piece)}
        className="relative block w-full overflow-hidden bg-[#ebe7e1] text-left"
        aria-label={`View ${piece.name}`}
      >
        <img
          src={piece.image}
          alt={piece.name}
          className="aspect-[0.82] w-full object-cover object-center
            transition-all duration-300 ease-out md:group-hover:scale-[1.03]
            md:group-hover:brightness-[0.72] sm:aspect-[0.78] lg:aspect-[0.76]"
        />
        {/* Desktop black hover gradient */}
        <div
          aria-hidden="true"
          className="
            pointer-events-none absolute inset-0 hidden
            bg-[linear-gradient(180deg,rgba(0,0,0,0.18)_0%,rgba(0,0,0,0.05)_35%,rgba(0,0,0,0.46)_100%)]
            opacity-0 transition-opacity duration-300
            md:block md:group-hover:opacity-100
          "
        />
      </button>
      {/* PRODUCT DETAILS */}
      <div className="px-2 pb-2 pt-2 sm:px-2.5">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => onOpenProduct(piece)}
            className="min-w-0 truncate text-left font-helvena text-[12px] uppercase leading-none text-black hover:underline md:text-[14px]"
          >
            {piece.displayId} — {piece.name}
          </button>
          <span className="shrink-0 font-helvena text-[12px] leading-none text-black md:text-[14px]">
            {displayedPrice}
          </span>
        </div>
        {/* ACTIONS */}
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {/* ADD TO CART */}
          {selectedVariant?.isOutOfStock ? (
            <button
              type="button"
              disabled
              className="flex h-[40px] cursor-not-allowed items-center justify-center rounded-[5px] border border-[#B8B8B8] bg-[#B8B8B8] px-2.5 font-helvena text-[10px] md:text-[12px] uppercase text-white opacity-80 sm:px-3"
            >
              {copy.outOfStock}
            </button>
          ) : isStandaloneInCart ? (
            <button
              type="button"
              onClick={onGoToCart}
              className="flex h-[40px] items-center justify-center rounded-[5px] border border-black bg-black px-2.5 font-helvena text-[10px] md:text-[12px] uppercase text-white transition-opacity duration-200 hover:opacity-90 sm:px-3"
            >
              {copy.goToCart}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleAddToCart}
              disabled={!selectedVariant}
              className={[
                "flex h-[40px] items-center justify-center rounded-[5px] border px-2.5 font-helvena text-[10px] md:text-[12px] uppercase transition-colors duration-200 sm:px-3",
                !selectedVariant
                  ? "cursor-not-allowed border-[#B8B8B8] bg-[#B8B8B8] text-white opacity-70"
                  : "border-black text-black hover:bg-black hover:text-white",
              ].join(" ")}
            >
              {selectedVariant ? copy.addToCart : copy.selectSize}
            </button>
          )}
          {/* APPLE PAY */}
          <QuickPayButton
            express
            className="w-[120px] [&_[role=status]]:mt-[2px]"
            items={quickPayItems}
            amount={quickPayAmount}
            disabled={isQuickPayDisabled}
            buttonHeight={40}
            buttonRadius={5}
            applePayTheme="white-outline"
          />
          {/* SIZES */}
          <div className="ml-auto flex items-center gap-1">
            {piece.sizes?.map((size) => {
              const variantForSize = getVariantForSize(size);
              const isOutOfStock = variantForSize?.isOutOfStock === true;
              const isSelected = selectedSize === size;
              return (
                <button
                  key={size}
                  type="button"
                  disabled={isOutOfStock}
                  aria-pressed={isSelected}
                  onClick={() => setSelectedSize(size)}
                  className={`font-spaceMono flex h-[40px] min-w-[29px] items-center justify-center rounded-[5px] border border-[#E5E7EB] text-[10px] sm:text-[12px] transition-colors duration-200 sm:min-w-[32px] hover:opacity-[95%]
                    ${
                      isSelected
                        ? "bg-black text-white"
                        : "bg-white text-[#111827]"
                    }
                    ${
                      isOutOfStock
                        ? "cursor-not-allowed opacity-40"
                        : "hover:opacity-[95%]"
                    }`}
                >
                  {size}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </article>
  );
};


const FinalSaleSection = ({
  products,
  bundle,
  bundleProductCount,
  copy,
  language,
  onOpenProduct,
  onAddAllToCart,
  onApplePay,
}) => {
  const [activeIndex, setActiveIndex] = React.useState(0);
  const desktopSliderRef = React.useRef(null);

  const activeProduct = products[activeIndex];

  const handleDesktopScroll = () => {
    const slider = desktopSliderRef.current;
    if (!slider) return;
    const slideWidth = slider.clientWidth;
    const newIndex = Math.round(slider.scrollLeft / slideWidth);
    if (
      newIndex >= 0 &&
      newIndex < products.length &&
      newIndex !== activeIndex
    ) {
      setActiveIndex(newIndex);
    }
  };

  const scrollToDesktopProduct = (index) => {
    const slider = desktopSliderRef.current;
    if (!slider) return;
    slider.scrollTo({
      left: slider.clientWidth * index,
      behavior: "smooth",
    });
    setActiveIndex(index);
  };
  if (!activeProduct) return null;

  return (
    <section className="relative overflow-hidden pt-2 px-58 sm:pb-10 pb-16 md:px-[72px] md:pb-[72px]">
      {/* BACKGROUND */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 top-[36px] sm:top-[60px]"
      />
      {/* FINAL SALE CARD */}
      <div
        className="relative z-10 mx-auto grid max-w-[1260px]
            grid-cols-1 items-center gap-6
            overflow-hidden rounded-[10px] bg-white
            px-6 py-6
            lg:grid-cols-[0.9fr_1.1fr]
            lg:gap-[72px]
            shadow-[0px_4px_238.3px_0px_#00000033"
      >
        {/* PRODUCT */}
        <div className="min-w-0 w-full">
          <div
            ref={desktopSliderRef}
            onScroll={handleDesktopScroll}
            className="
                flex h-[360px] w-full
                snap-x snap-mandatory
                overflow-x-auto overflow-y-hidden
                scroll-smooth overscroll-x-contain
                rounded-[4px] bg-[#F3F0EA]
                [scrollbar-width:none]
                [&::-webkit-scrollbar]:hidden
                sm:h-[440px]
                md:h-[500px]
                lg:h-[557px]
              "
          >
            {products.map((product) => (
              <button
                key={product.id}
                type="button"
                onClick={() => onOpenProduct(product)}
                className="h-full w-full shrink-0 snap-center overflow-hidden text-left"
              >
                <img
                  src={product.image}
                  alt={product.name}
                  draggable={false}
                  className="h-full w-full select-none object-cover object-center"
                />
              </button>
            ))}
          </div>
          {/* PRODUCT NAME + PRICE */}
          <div className="flex items-center justify-between gap-3 px-1 py-2 sm:px-2 bg-[#FAF7F2]">
            <p className="min-w-0 truncate font-helvena text-[11px] uppercase text-black sm:text-[12px] md:text-[14px]">
              {activeProduct.displayId} — {activeProduct.name}
            </p>

            <span className="shrink-0 font-helvena text-[11px] text-black sm:text-[12px] md:text-[14px]">
              {activeProduct.price}
            </span>
          </div>
          {/* CAROUSEL INDICATORS */}
          <div className="mt-3 flex items-center justify-center gap-[5px] sm:mt-5">
            {products.map((product, index) => {
              const isActive = index === activeIndex;
              return (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => scrollToDesktopProduct(index)}
                  aria-label={`View ${product.name}`}
                  className={`h-[5px] rounded-full bg-black transition-all duration-300 sm:h-[6px] ${
                    isActive ? "w-[42px] sm:w-[68px]" : "w-[10px] sm:w-[13px]"
                  }`}
                />
              );
            })}
          </div>
        </div>
        {/* CONTENT */}
        <div className="min-w-0 pb-2 pt-1 lg:mb-5 lg:pb-0 lg:pr-6 lg:pt-0">
          <p className="font-spaceMono text-[10px] font-bold uppercase leading-[1.5] tracking-[0.06em] text-[#A64346] sm:text-[12px] md:text-[14px]">
            {copy.finalSaleEyebrow}
          </p>
          <h2 className="mt-3 font-inter text-[28px] font-bold leading-[1.05] tracking-[-0.04em] text-[#111827] sm:text-[34px] md:text-[40px] lg:mt-4 lg:text-[48px]">
            {copy.finalSaleTitle}
          </h2>
          <p className="mt-4 max-w-[610px] font-inter text-[12px] font-normal leading-[1.65] text-[#6B7280] sm:text-[14px] md:text-[16px] lg:mt-5 lg:text-[18px]">
            {copy.finalSaleDescription}          
          </p>

          {/* PAYMENT */}
          <div className="mt-6 sm:mt-8 lg:mt-[72px]">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={onApplePay}
                className="
                    flex h-[40px] w-[157px] items-center justify-center gap-1.5
                    rounded-[3px] border border-[#9494944D]
                    bg-white px-5 text-[15px] font-semibold text-black
                    sm:w-auto sm:min-w-[157px]
                  "
              >
                <ApplePayIcon className="mb-[2px] h-[18px] w-auto" />
                <span>Pay</span>
              </button>
            </div>
            <p className="mt-3 font-inter text-[11px] leading-[1.5] text-[#0F0F10] sm:text-[12px] md:text-[14px]">
              {copy.expressPay}{" "}
              <span className="font-semibold">
                {copy.bundleCollection} {bundle?.discountPercent || 0}% {copy.off}
              </span>
            </p>
            <button
              type="button"
              onClick={onAddAllToCart}
              className="
                  mt-4 flex h-[40px] min-w-[157px] md:h-[43px]
                  items-center justify-center rounded-[5px]
                  border border-black bg-white px-5
                  font-spaceMono text-[10px] font-bold uppercase
                  tracking-[0.16em] text-black
                  transition-colors whitespace-nowrap sm:whitespace-nowrap
                  hover:bg-black hover:text-white
                  sm:w-auto sm:min-w-[197px] sm:text-[11px]
                  md:text-[13px]
                "
            >
              {copy.addAllPrefix} {bundleProductCount} {copy.addAllSuffix}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

const EftakhariCollectionPage = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = React.useState("en");
  const copy = collectionTranslations[language];
  const toggleLanguage = React.useCallback(() => {
    setLanguage((current) => (current === "en" ? "de" : "en"));
  }, []);
  const [isCartDrawerOpen, setIsCartDrawerOpen] = React.useState(false);
  const [cartDrawerProductId, setCartDrawerProductId] = React.useState(null);
  const [cartDrawerBundleId, setCartDrawerBundleId] = React.useState(null);
  const [cartDrawerBundleInstanceId, setCartDrawerBundleInstanceId] =
    React.useState(null);
  const {
    collectionPieces,
    bundleDetails,
    bundleProducts,
    isLoading: isExclusiveProductsLoading,
    isFetching: isExclusiveProductsFetching,
    isError: isExclusiveProductsError,
    isSizingModalOpen,
    sizingModalMode,
    openBundleModal,
    closeBundleModal,
    confirmBundle,
  } = useExclusiveBundleFlow({
    copy,
    onBundleAdded: ({
      bundleId,
      bundleInstanceId,
    }) => {
      setCartDrawerProductId(null);
      setCartDrawerBundleId(bundleId);
      setCartDrawerBundleInstanceId(
        bundleInstanceId,
      );
      setIsCartDrawerOpen(true);
    },
  });

  const handleOpenCollectionCartModal = React.useCallback(() => {
    openBundleModal("cart");
  }, [openBundleModal]);

  const handleOpenCollectionApplePayModal = React.useCallback(() => {
    openBundleModal("applePay");
  }, [openBundleModal]);

  const handleOpenProduct = React.useCallback(
    (piece) => {
      if (!piece?.slug) return;

      navigate(`/product/${piece.slug}`, {
        state: {
          slug: piece.slug,
          productType: "exclusive",
          product_type: "exclusive",
        },
      });
    },
    [navigate],
  );

  const handleProductAddedToCart = React.useCallback((productId) => {
    setCartDrawerBundleId(null);
    setCartDrawerBundleInstanceId(null);
    setCartDrawerProductId(productId);
    setIsCartDrawerOpen(true);
  }, []);
  const isProductsLoading =
    isExclusiveProductsLoading || isExclusiveProductsFetching;

  return (
    <div className="relative bg-[#f7f5f1]">
      <SEO
        title="Amir Eftekhari Collection | Limited-Edition Fashion | Eveness"
        description="Discover the Amir Eftekhari Collection by Eveness, featuring limited-edition contemporary fashion made in London and inspired by Persian heritage."
        path="/eftakhari-collection"
      />
      <NewNavbar
        mobileOverlayOnContent
        showAmirCollection
        showCollectionLanguageSelector
        collectionLanguage={language}
        onCollectionLanguageChange={toggleLanguage}
      />
      <main className="min-h-screen text-[#111827]">
        <section className="mx-auto grid max-w-[1536px] grid-cols-1 md:mt-5 lg:grid-cols-2">
          {/* LEFT CONTENT */}
          <div className="order-2 flex min-h-0 flex-col bg-white px-2.5 pb-5 pt-3 lg:order-1 lg:min-h-[640px] lg:px-[80px] lg:py-[92px]">
            <SectionLabel number="Eveness">Amir Collection</SectionLabel>

            <h1 className="mt-3 max-w-[240px] md:max-w-[450px] text-[20px] font-normal leading-[120%] tracking-[-0.035em] text-[#111827] sm:text-[30px] lg:mt-6 md:text-[38px]">
              {copy.heroTitle}
            </h1>

            {/* DESKTOP ONLY CONTENT */}
            <div className="mt-auto pt-5 md:pt-20 block">
              <p className="max-w-[570px] font-montserrat text-[10px] md:text-[15px] leading-[1.75] text-[#6B7280]">
                {copy.heroDescription}
              </p>

              <div className="mt-7 grid max-w-[620px] grid-cols-2 border-y border-[#E5E7EB]">
                <div className="py-6 pr-4">
                  <p className="font-spaceMono text-[12px] font-bold uppercase text-[#6B7280]">
                    {copy.launchDate}
                  </p>

                  <p className="mt-2 font-montserrat text-[12px] md:text-[14px] font-medium text-[#111827]">
                    {language === "de" ? "24.09.26" : "9/24/26"}
                  </p>
                </div>

                <div className="py-6 pl-4">
                  <p className="font-spaceMono text-[12px] font-bold uppercase text-[#6B7280]">
                   {copy.collectionCloses}
                  </p>

                  <p className="mt-2 font-montserrat text-[12px] md:text-[14px] font-medium text-[#111827]">
                    {language === "de" ? "02.10.26" : "10/02/26"}                    
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* HERO IMAGE */}
          <div className="order-1 w-full overflow-hidden lg:order-2 lg:max-h-[700px]">
            <img
              src={amirCollectionHeroImage}
              alt="Amir Collection"
              className="aspect-[0.84] md:aspect-[0.95] w-full object-cover object-top-left md:object-[40%_60%] md:max-h-[700px]"
            />
          </div>
        </section>

        {/* CURATED PIECES */}
        <section className="mx-auto max-w-[1536px] bg-[#FAF7F2] px-3 pb-4 pt-0 sm:px-5 md:px-8 md:pb-8 md:pt-8 lg:px-[34px]">
          {/* QUOTE */}
          <div className="mx-auto max-w-[720px] text-center font-inter text-[12px] text-[#0F0F10] md:text-[16px]">
            “{copy.quote}”
          </div>
          <div className="justify-center flex text-[12px] text-[#0F0F10] md:text-[16px]">
            <ul className="list-disc">
              <li className="font-normal">Amir Eftekhari</li>
            </ul>
          </div>
          {/* PRODUCTS */}
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:mt-8 lg:grid-cols-3 lg:gap-5">
            {isProductsLoading ? (
              <div className="col-span-full flex min-h-[320px] items-center justify-center text-sm text-[#6B7280]">
                {copy.loadingCollection}
              </div>
            ) : isExclusiveProductsError ? (
              <div className="col-span-full flex min-h-[220px] items-center justify-center text-sm text-red-500">
                {copy.failedCollection}
              </div>
            ) : collectionPieces.length > 0 ? (
              collectionPieces.map((piece) => (
                <CollectionProductCard
                  key={piece.id}
                  piece={piece}
                  copy={copy}
                  onOpenProduct={handleOpenProduct}
                  onProductAddedToCart={handleProductAddedToCart}
                  onGoToCart={() => navigate("/cart")}
                />
              ))
            ) : (
              <div className="col-span-full flex min-h-[100px] items-center justify-center text-sm text-[#6B7280]">
                {copy.noProducts}
              </div>
            )}
          </div>
          {collectionPieces.length > 0 && (
            <div className="mt-4 text-center">
              <p className="font-spaceMono text-[10px] font-semibold uppercase tracking-[0.08em] text-[#A64346] md:text-[12px]">
                {copy.madeToOrder}
              </p>
              <p className="mt-1.5 font-spaceMono text-[10px] font-medium uppercase tracking-[0.06em] text-[#6B7280] md:text-[12px]">
                {copy.shippingNotice}
              </p>
            </div>
          )}
          {/* BUNDLE PURCHASE */}
          {collectionPieces.length > 0 && (
            <div className="mt-7 flex flex-col items-center justify-center text-center mx-auto md:mt-8">
              <p className="font-inter text-[11px] font-normal leading-relaxed text-[#0F0F10] md:text-[14px]">
                {copy.expressPay}{" "}
                <span className="font-semibold">
                  {language === "de" ? (
                    <>
                      {copy.bundleCollection} {bundleDetails?.discountPercent ?? 0}%{" "}
                      {copy.off}
                    </>
                  ) : (
                    <>
                      {copy.bundleCollection} {bundleDetails?.discountPercent ?? 0}%{" "}
                      {copy.off}
                    </>
                  )}
                </span>
              </p>
              {/* ADD ALL TO CART */}
              <button
                type="button"
                onClick={handleOpenCollectionCartModal}
                className="mt-7 flex h-[40px] md:h-[43px] min-w-[166px] items-center justify-center rounded-[5px] border border-black bg-transparent px-5 font-spaceMono text-[11px] font-bold uppercase tracking-[0.18em] text-black transition-colors duration-200 hover:bg-black hover:text-white md:min-w-[197px] md:text-[13px]"
              >
                {copy.addAllPrefix} {bundleProducts.length} {copy.addAllSuffix}
              </button>
            </div>
          )}
        </section>

        {/* FOCUS PIECE / GALLERY */}
        <section className="bg-white pt-8 md:pt-10">
          {/* SECTION TITLE */}
          <div className="px-3 sm:px-5 md:px-8 lg:px-[80px]">
            <SectionLabel number="04">{copy.focusPiece}</SectionLabel>
          </div>
          {/* MOBILE GALLERY */}
          <div className="mt-5 px-1.5 pb-4 md:hidden">
            <div className="grid grid-cols-2 items-start gap-1.5">
              {/* LEFT COLUMN */}
              <div className="flex flex-col gap-1.5">
                <img
                  src={galleryImages[0].src}
                  alt="Amir Collection"
                  className="aspect-[0.82] w-full rounded-[3px] object-cover"
                />

                <img
                  src={galleryImages[4].src}
                  alt="Amir Collection"
                  className="aspect-[0.78] w-full rounded-[3px] object-cover"
                />

                <img
                  src={galleryImages[1].src}
                  alt="Amir Collection"
                  className="aspect-[1.55] w-full rounded-[3px] object-cover"
                />

                <div className="aspect-[0.95] w-full overflow-hidden rounded-[3px]">
                  <img
                    src={galleryImages[8].src}
                    alt="Amir Collection"
                    className="h-full w-full scale-[1.20] object-cover object-center"
                  />
                </div>
              </div>

              {/* RIGHT COLUMN */}
              <div className="flex flex-col gap-1.5">
                <img
                  src={galleryImages[2].src}
                  alt="Amir Collection"
                  className="aspect-[1.12] w-full rounded-[3px] object-cover"
                />

                <img
                  src={galleryImages[7].src}
                  alt="Amir Collection"
                  className="aspect-[0.82] w-full rounded-[3px] object-cover"
                />
                <img
                  src={galleryImages[3].src}
                  alt="Amir Collection"
                  className="aspect-[0.78] w-full rounded-[3px] object-cover"
                />

                <div className="aspect-[1.24] w-full overflow-hidden rounded-[3px]">
                  <img
                    src={galleryImages[5].src}
                    alt="Amir Collection"
                    className="h-full w-full scale-[1.05] object-cover object-[0%_55%]"
                  />
                </div>
              </div>
            </div>
          </div>
          {/* DESKTOP GALLERY */}
          <div className="mx-auto mt-8 hidden max-w-[1536px] grid-cols-3 gap-4 px-8 md:grid lg:mt-16 lg:gap-5 lg:px-[80px]">
            {/* COLUMN 1 */}
            <div className="space-y-5">
              <img
                src={galleryImages[6].src}
                alt=""
                className="aspect-[0.87] w-full rounded-[5px] object-cover"
              />

              <img
                src={galleryImages[1].src}
                alt=""
                className="aspect-[1.07] w-full rounded-[5px] object-cover"
              />
              <img
                src={galleryImages[0].src}
                alt="Amir Collection"
                className="aspect-[0.81] w-full rounded-[5px] object-cover"
              />
            </div>
            {/* COLUMN 2 */}
            <div className="space-y-5">
              <img
                src={galleryImages[2].src}
                alt=""
                className="aspect-[1.07] w-full rounded-[5px] object-cover"
              />

              <img
                src={galleryImages[3].src}
                alt=""
                className="aspect-[0.97] w-full rounded-[5px] object-cover"
              />
              <img
                src={galleryImages[8].src}
                alt=""
                className="aspect-[0.74] w-full rounded-[5px] object-cover"
              />
            </div>
            {/* COLUMN 3 */}
            <div className="space-y-5">
              <img
                src={galleryImages[7].src}
                alt=""
                className="aspect-[0.87] w-full rounded-[5px] object-cover"
              />

              <img
                src={galleryImages[5].src}
                alt=""
                className="aspect-[1.07] w-full rounded-[5px] object-cover"
              />
              <img
                src={galleryImages[4].src}
                alt=""
                className="aspect-[0.81] w-full rounded-[5px] object-cover"
              />
            </div>
          </div>
          {/* STORY */}
          <div className="flex w-full flex-col px-3 pb-10 pt-8 sm:px-5 md:px-8 md:pt-10 lg:px-[80px] bg-[#FAF7F2]">
            <SectionLabel number="04">{copy.story}</SectionLabel>
            <h1 className="mt-3 w-full text-[20px] font-normal leading-[120%] tracking-[-0.035em] text-[#111827] sm:text-[30px] md:text-[38px] lg:mt-8">
              {copy.storyTitle}
            </h1>
          </div>
        </section>
        {/* FINAL SALE / FEATURE PRODUCT */}
        {collectionPieces.length > 0 ? (
          <FinalSaleSection
            products={collectionPieces}
            bundle={bundleDetails}
            bundleProductCount={bundleProducts.length}
            copy={copy}
            language={language}
            onOpenProduct={handleOpenProduct}
            onAddAllToCart={handleOpenCollectionCartModal}
            onApplePay={handleOpenCollectionApplePayModal}
          />
        ) : (
          <div className="flex min-h-[100px] justify-center pt-10 items-center text-sm text-[#6B7280] px-3 md:px-0">
            No collection products are available at the moment. Please check
            back soon!
          </div>
        )}
      </main>
      <ExclusiveBundleSizingModal
        isOpen={isSizingModalOpen}
        products={bundleProducts}
        bundle={bundleDetails}
        copy={copy}
        mode={sizingModalMode}
        onClose={closeBundleModal}
        onConfirm={confirmBundle}
      />
      <Footer />
      <AddedToCartDrawer
        isOpen={isCartDrawerOpen}
        productId={cartDrawerProductId}
        bundleId={cartDrawerBundleId}
        bundleInstanceId={cartDrawerBundleInstanceId}
        onViewProduct={handleOpenProduct}
        onClose={() => {
          setIsCartDrawerOpen(false);
        }}
        onCheckout={({ productId, bundleId, bundleInstanceId }) => {
          const isAuthenticated = Boolean(localStorage.getItem("authToken"));
          const isBundleCheckout = Boolean(bundleInstanceId || bundleId);
          const checkoutState = isBundleCheckout
            ? {
                checkoutMode: "bundle",
                checkoutBundleId: bundleId,
                checkoutBundleInstanceId: bundleInstanceId,
              }
            : {
                checkoutMode: "single",
                checkoutProductId: productId,
              };
          if (isAuthenticated)
            navigate("/shipping-address", { state: checkoutState });
          else navigate("/order-information", { state: checkoutState });
        }}
      />
    </div>
  );
};

export default EftakhariCollectionPage;
